import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, ScanSearch, Scale, type LucideIcon } from "lucide-react";
import { container, gutters } from "@/components/ds";
import { useListings, useVendors } from "@/hooks/useDatabase";
import { cn } from "@/lib/utils";
import { useHomeCompounds } from "@/components/home/homeData";
import { HomeHeading, homeSectionY, LateNumber } from "@/components/home/parts";

/**
 * Homepage "How this site works", right under the prices it explains.
 *
 * How vendors are scored, where prices come from and what the write-ups are based on, each with a link to the
 * page that backs it. Every sentence restates something those pages already say (methodology, editorial
 * standards), so check them before changing the wording here.
 *
 * The wording is the owner's (he removed the earnings item on 2026-10-22): the 2026-10-22 rebuild and the
 * 2026-10-24 restyle (direction B: the figures in Inter Black on one card, each point a card with a tinted icon)
 * changed the layout only. The three figures are counted from the database, the same counts the old SocialProofBar
 * showed, so they cannot drift from the rest of the site.
 */
const POINTS: { icon: LucideIcon; tone: string; title: string; body: string; to: string; link: string }[] = [
  {
    icon: Scale,
    tone: "tone-blue",
    title: "Scores you can check",
    body: "Every vendor gets a Trust Score from 0 to 100 on the same five public criteria. Third-party lab certificates carry the most weight.",
    to: "/vendors/methodology",
    link: "Read the scoring method",
  },
  {
    icon: ScanSearch,
    tone: "tone-violet",
    title: "Prices an editor approves",
    body: "A scraper flags price changes and an editor approves them. It cannot read every store, so check the vendor before you buy.",
    to: "/editorial-standards",
    link: "How prices are tracked",
  },
  {
    icon: BookOpen,
    tone: "tone-teal",
    title: "Published studies first",
    body: "Compound profiles start from PubMed and ClinicalTrials.gov. If there is clinical data, we use it.",
    to: "/editorial-standards",
    link: "Editorial standards",
  },
];

export default function HomeTrust() {
  const { data: compounds = [], isPending: compoundsPending } = useHomeCompounds();
  const { data: listings = [], isPending: listingsPending } = useListings();
  const { data: vendors = [], isPending: vendorsPending } = useVendors();

  const stats = [
    { count: compounds.length, label: "Compound profiles", loading: compoundsPending && compounds.length === 0 },
    { count: listings.length, label: "Vendor price listings", loading: listingsPending && listings.length === 0 },
    { count: vendors.length, label: "Vendors tracked", loading: vendorsPending && vendors.length === 0 },
  ];

  return (
    <section aria-labelledby="home-trust-title" className={cn(container.default, gutters, homeSectionY)}>
      <HomeHeading
        id="home-trust-title"
        title="How this site works"
        summary="Here is how we score vendors, check prices and choose sources. Each point links to the page with the full rules."
      />
      <dl className="home-card grid grid-cols-3 divide-x divide-border">
        {stats.map((s) => (
          // Reversed so the number shows first while the markup keeps label then value.
          <div key={s.label} className="flex min-w-0 flex-col-reverse justify-end px-3 py-4 sm:px-6 sm:py-5">
            <dt className="type-small mt-1 text-muted-foreground">{s.label}</dt>
            <dd className="text-2xl font-black tabular-nums tracking-tight text-foreground sm:text-3xl">
              {/* A failed request leaves the figure blank rather than claiming zero. */}
              <LateNumber loading={s.loading} value={s.count > 0 ? s.count.toLocaleString("en-US") : " "} />
            </dd>
          </div>
        ))}
      </dl>
      {/* Each point is one link to the page that backs it: the whole card is the target, the last line names it. */}
      <ul className="mt-4 grid gap-3 sm:mt-6 lg:grid-cols-3 lg:gap-4">
        {POINTS.map(({ icon: Icon, tone, title, body, to, link }) => (
          <li key={title} className="min-w-0">
            <Link to={to} className={cn("home-card home-card-link group flex h-full flex-col p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-5", tone)}>
              <h3 className="type-body flex items-center gap-3 font-bold text-foreground">
                <span aria-hidden className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[rgb(var(--tone)/0.12)]">
                  <Icon className="tone-ink h-5 w-5" strokeWidth={2} />
                </span>
                {title}
              </h3>
              <p className="type-small mt-3 text-muted-foreground">{body}</p>
              <span className="type-small mt-auto inline-flex items-center gap-1 pt-3 font-medium text-primary group-hover:underline group-hover:underline-offset-4">
                {link} <ArrowRight aria-hidden className="h-4 w-4" />
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <p className="type-small mt-6 text-muted-foreground">
        Everything listed here is for laboratory and research use only. Nothing on this site is medical advice.
      </p>
    </section>
  );
}
